import { Link } from 'found';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import getFullAddressString from 'src/util/address';

import TradeRuleListTradePoints from './TradeRuleListTradePoints';

const TradeRuleListFragment = graphql`
  fragment TradeRuleList_property on Property 
  @throwOnFieldError
  @argumentDefinitions(
    first: { type: "Int" }
    after: { type: "String" }
    last: { type: "Int" }
    before: { type: "String" }
  ) {
    uuid
    title
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    ...TradeRuleListTradePoints_property @arguments(
      first: $first
      after: $after
      last: $last
      before: $before
    )
  }
`;

/**
 * TradeRuleList functional component displays trade rules grouped by trade point.
 * @param {object} props - Component props.
 * @param {object} props.property - Property fragment reference.
 * @returns {React.ReactElement} TradeRuleList component.
 */
function TradeRuleList({ property = null }) {
  const intl = useIntl();
  const propertyData = useFragment(TradeRuleListFragment, property);

  if (!propertyData) {
    return <Loading />;
  }

  const { address, uuid, title } = propertyData;

  const addr = getFullAddressString(address);
  const pageTitle = intl.formatMessage(
    { id: 'trade_rule.trade_rule_list.page.title', defaultMessage: 'Enosi - Property: {title} - Trade Rules' },
    { title },
  );

  const propertyControls = (
    <ButtonToolbar>
      <ButtonGroup className="ms-2">
        <Link to={`/properties/${uuid}/trade-rules/active`} className="btn btn-darken">
          <FormattedMessage id="trade_rule.trade_rule_list.active_trade_rules.label" defaultMessage="Active trade rules" />
        </Link>
      </ButtonGroup>

      <ButtonGroup className="ms-2">
        <Link to={`/properties/${uuid}/settings`} className="btn btn-darken">
          <FormattedMessage id="trade_rule.trade_rule_list.property_settings.label" defaultMessage="Property Settings" />
        </Link>
      </ButtonGroup>
    </ButtonToolbar>
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          {
            name: <FormattedMessage id="trade_rule.trade_rule_list.properties.breadcrumb" defaultMessage="Properties" />, path: '/properties',
          },
          { name: title, path: `/properties/${uuid}` },
          { name: <FormattedMessage id="trade_rule.trade_rule_list.trade_rules.breadcrumb" defaultMessage="Trade Rules" /> },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr}
        context={<FormattedMessage id="trade_rule.trade_rule_list.heading.trade_rules.title" defaultMessage="Trade Rules" />}
        controls={propertyControls}
      />
      <TradeRuleListTradePoints property={propertyData} date={DateTime.local()} />
    </>
  );
}

TradeRuleList.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default TradeRuleList;
